/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorological Institute (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

export const MS = 1000;
export const MIN_MS = 60 * MS;
export const HOUR_MS = 60 * MIN_MS;

export const DAY_MS = 24 * HOUR_MS;
export const WEEK_MS = 7 * DAY_MS;

export const FIVE_MIN_MS = 5 * 60 * 1000;
export const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

// We work mostly with radar data every 5 minutes
export const roundToNearest5Min = (dt: Date): Date =>
  new Date(Math.round(dt.getTime() / FIVE_MIN_MS) * FIVE_MIN_MS);

// Given a timestamp, floor to the closest 5-minute interval (or any number of ms interval)
export const floorToInterval = (
  ms: number,
  intervalMs: number,
  anchorMs = 0,
): number => {
  const adj = (((ms - anchorMs) % intervalMs) + intervalMs) % intervalMs;
  return ms - adj;
};

// Given a timestamp, ceil to the closest 5-minute interval (or any number of ms interval)
export const ceilToInterval = (ms: number, intervalMs: number): number => {
  if (intervalMs <= 0) {
    return ms;
  }
  return Math.ceil(ms / intervalMs) * intervalMs;
};

export const parseDatetimeRange = (
  datetime?: string | null,
): [number, number] | null => {
  if (!datetime) {
    return null;
  }
  const parts = datetime.split('/');
  if (parts.length !== 2) {
    return null;
  }
  const from = new Date(parts[0]);
  const to = new Date(parts[1]);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return null;
  }
  return [from.getTime(), to.getTime()];
};

export const buildContinuousTimes = (
  t0: Date,
  t1: Date,
  stepMs: number,
): Date[] => {
  const start = ceilToInterval(t0.getTime(), stepMs);
  const end = floorToInterval(t1.getTime(), stepMs);
  const result: Date[] = [];
  for (let ms = start; ms <= end; ms += stepMs) {
    result.push(new Date(ms));
  }
  return result;
};

/**
 * Given a pixel x-position, find the closest time in a D3 scaleBand.
 * Returns null when times is empty.
 */
export const closestTimeBand = (
  xPixel: number,
  times: Date[],
  bandStart: (t: Date) => number,
  bandwidth: number,
): Date | null => {
  if (times.length === 0) {
    return null;
  }
  const center = (t: Date): number => bandStart(t) + bandwidth / 2;
  let best = times[0];
  let bestDist = Math.abs(xPixel - center(best));
  for (let i = 1; i < times.length; i += 1) {
    const dist = Math.abs(xPixel - center(times[i]));
    if (dist < bestDist) {
      best = times[i];
      bestDist = dist;
    }
  }
  return best;
};

export const pickCentralWindow = (
  minMs: number,
  maxMs: number,
  stepMs: number,
  desiredWindowMs: number,
): [number, number] => {
  const lo = floorToInterval(minMs, stepMs);
  const hi = ceilToInterval(maxMs, stepMs);
  const span = Math.max(0, hi - lo);

  if (span <= desiredWindowMs) {
    return [lo, hi];
  }

  const mid = lo + span / 2;
  let from = mid - desiredWindowMs / 2;
  let to = mid + desiredWindowMs / 2;

  if (from < lo) {
    to += lo - from;
    from = lo;
  }
  if (to > hi) {
    from -= to - hi;
    to = hi;
  }

  from = floorToInterval(from, stepMs);
  to = ceilToInterval(to, stepMs);

  from = Math.max(lo, Math.min(from, hi));
  to = Math.max(lo, Math.min(to, hi));

  if (from > to) {
    return [lo, hi];
  }
  return [from, to];
};
